import { DAY_WEIGHT } from "constants/constants";

import { format } from "date-fns";
import { getTimeStampFromDateString } from "components/kelas-live/utils";
import { getDayMap, getIndonesianTimezone } from "utils/utils";
import { DaySchedule } from "../../../hooks/use-live-classes/use-live-classes.types";

export interface CourseScheduleItem {
  isActive: boolean;
  schedules: Array<{
    days: string[];
    startTime: string;
    endTime: string;
    course: {
      optional?: boolean;
      subject_themes?: {
        name: string;
        meta: {
          text_colour: string;
          background_colour: string;
          icon_small: string;
        };
      };
    };
  }>;
}

interface SubjectThemeMeta {
  text_colour: string;
  background_colour: string;
  icon_small: string;
}

interface SubjectTheme {
  name: string;
  meta: SubjectThemeMeta;
}

interface Course {
  subject_themes?: SubjectTheme;
  optional?: boolean;
}

interface DayScheduleSlot {
  course: Course;
  startTime: string;
  endTime: string;
}

interface ScheduleMap {
  [day: string]: DayScheduleSlot[];
}

export const getWeeklyClassSchedule = (
  courses: CourseScheduleItem[],
  mediaBaseUrl: string
): DaySchedule[] => {
  const activeCourses = courses.filter((c) => c.isActive);

  const scheduleMap: ScheduleMap = {};

  activeCourses.forEach((c) => {
    c.schedules.forEach((schedule) => {
      if (!schedule.course.optional) {
        schedule.days.forEach((day) => {
          if (!scheduleMap[day]) {
            scheduleMap[day] = [];
          }
          // Add the schedule slot
          scheduleMap[day].push({
            course: schedule.course,
            startTime: schedule.startTime,
            endTime: schedule.endTime
          });
        });
      }
    });
  });

  // Sort and display in local time for each day's schedule by start time
  Object.keys(scheduleMap).forEach((day) => {
    scheduleMap[day].sort((a, b) => {
      const timeA = new Date(a.startTime).toLocaleTimeString("en-US", {
        hour12: false
      });
      const timeB = new Date(b.startTime).toLocaleTimeString("en-US", {
        hour12: false
      });

      // Compare the time portions
      return timeA.localeCompare(timeB);
    });
  });
  const sortedResult = sortScheduleByDayWeight(scheduleMap);
  const result = transformScheduleData(sortedResult, mediaBaseUrl);

  return result;
};
const sortScheduleByDayWeight = (scheduleMap: ScheduleMap): ScheduleMap => {
  const sortedDays = Object.keys(scheduleMap).sort(
    (a, b) => DAY_WEIGHT[a] - DAY_WEIGHT[b]
  );

  const sortedSchedule: ScheduleMap = {};

  sortedDays.forEach((day) => {
    sortedSchedule[day] = scheduleMap[day];
  });
  return sortedSchedule;
};

const transformScheduleData = (
  schedule: ScheduleMap,
  mediaBaseUrl: string
): DaySchedule[] => {
  const scheduleData = Object.keys(schedule).map((dayKey, index) => {
    return {
      id: (index + 1).toString(),
      day: getDayMap[dayKey],
      session: schedule[dayKey].map((slot: DayScheduleSlot, idx: number) => ({
        id: (idx + 1).toString(),
        subject: slot.course.subject_themes?.name ?? "",
        time: `${format(
          getTimeStampFromDateString(slot.startTime),
          "k:mm"
        )} - ${format(
          getTimeStampFromDateString(slot.endTime),
          "k:mm"
        )} ${getIndonesianTimezone()}`,
        text_color: slot.course.subject_themes?.meta.text_colour ?? "",
        background_colour:
          slot.course.subject_themes?.meta.background_colour ?? "",
        icon: slot.course.subject_themes
          ? mediaBaseUrl + "/" + slot.course.subject_themes.meta.icon_small
          : ""
      }))
    };
  });

  return scheduleData;
};
